import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { noop } from 'ui-components/default-props/noop';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

import { useModal } from '../../hooks/use-modal';
import { getPrepaidDuty } from '../../store/csw/csw.helpers';
import * as UserActions from '../../store/user/user.actions';

import PrepayDutyAlertModal from './alert-modal';
import { getUserSettingPrepayDutyAlert, createUserSettingPrepayDutyAlertFromSetting, shouldShowPrepayDutyAlert } from './helpers';
import { Recipient } from './constants';

/**
 * @typedef {import('../../prop-types/shipping-options').ShippingOptionsPropType} OtherShippingOptions
 */

/**
 * @typedef {import('../../fulfillment-planner/duties-and-taxes/prop-types').AvailableDutyOption} AvailableDutyOption
 */

/**
 * @param {object} props
 * @param {(object: { onActionTaken: () => void }) => React.JSX.Element} props.children
 * @param {AvailableDutyOption[]|Readonly<object[]>?} props.availableDutiesOptions
 * @param {boolean?} props.canShowPrompt
 * @param {() => void?} props.onContinueToPurchase
 * @param {() => void?} props.onSelectedDutyOptionChange
 * @param {OtherShippingOptions|Readonly<object>} props.otherShippingOptions
 * @returns {React.JSX.Element}
 */
const PrepayDutyPromptOnAction = ({
  children,
  availableDutiesOptions = emptyArray,
  canShowPrompt = false,
  onContinueToPurchase = noop,
  onSelectedDutyOptionChange = noop,
  otherShippingOptions = emptyObject,
}) => {
  const dispatch = useDispatch();
  const { open, close, isOpen } = useModal();
  /**
   * @type {ReturnType<typeof useState<AvailableDutyOption[]|Readonly<AvailableDutyOption[]>>>}
   */
  const [resolvedDutyOptions, setResolvedDutyOptions] = useState([]);

  const prepayDutySetting = useSelector(getUserSettingPrepayDutyAlert);
  const shouldPromptBasedOnSettings = useSelector(shouldShowPrepayDutyAlert);

  const onActionTaken = useCallback(async () => {
    const prepaidDuty = getPrepaidDuty({ otherShippingOptions });
    if (shouldPromptBasedOnSettings) {
      if (availableDutiesOptions?.length) {
        if (prepaidDuty === Recipient) {
          setResolvedDutyOptions(availableDutiesOptions);
          open();
          return;
        }
      }
      const updatedSetting = createUserSettingPrepayDutyAlertFromSetting(prepayDutySetting, {
        createdPrepayDutyOn: moment().toISOString(),
      });
      dispatch(UserActions.saveUserSetting(updatedSetting));
    }
    onContinueToPurchase();
  }, [availableDutiesOptions, dispatch, onContinueToPurchase, open, otherShippingOptions, prepayDutySetting, shouldPromptBasedOnSettings]);

  const _onSelectedDutyOptionChange = useCallback(
    (selectedDutyOption) => {
      onSelectedDutyOptionChange(selectedDutyOption);
      close();
    },
    [close, onSelectedDutyOptionChange],
  );

  if (!canShowPrompt) {
    return children({ onActionTaken: onContinueToPurchase });
  }

  return (
    <>
      {children({ onActionTaken })}
      {isOpen && (
        <PrepayDutyAlertModal
          availableDutiesOptions={resolvedDutyOptions}
          onChange={_onSelectedDutyOptionChange}
          onClose={close}
          onContinue={onContinueToPurchase}
        />
      )}
    </>
  );
};

export default PrepayDutyPromptOnAction;
